import React, { useEffect, useState } from 'react';
import ModalShell from '../ui/ModalShell';
import { Fuel, Plus, Search, Loader2 } from 'lucide-react';
import { SHEET_TOKENS, getGlassSurface, getTextColors, getInnerCardSurface } from '../../pages/MojeSmena/sheetStyle';
import { SheetButton } from '../../pages/MojeSmena/SheetUI';

const SECTION_COLOR = '#0CC0DF';

// POZOR: Field MUSÍ být mimo TankovaniModal — pokud ho definujeme dovnitř,
// každý render vytvoří novou komponent referenci, React unmount/remount celý
// subtree a input ztratí focus (kurzor "skáče" na první/autofocus pole).
function Field({ label, children, required, labelColor }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{
                fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                color: labelColor,
            }}>
                {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
            </span>
            {children}
        </label>
    );
}

/**
 * Rychlý modal pro zaznamenání tankování. Otevírá se z karty vozidla
 * v Inspectoru (tlačítko "Tankování" v sekci akcí). Pole jsou předvyplněna
 * podle kontextu (vozidlo, řidič, km_stav, typ paliva).
 *
 * Trojice litry / cena_l / celkem je provázaná: zadáním kterýchkoli dvou
 * se třetí dopočítá. Backend ukládá jen litry + cena_kc_litr (spotřeba se
 * dopočte z předchozího tankování).
 *
 * Po úspěchu volá `onSaved(record)` — parent typicky refetchne vozidla,
 * aby se aktualizoval `km_stav` a `palivo_pct` v Inspectoru.
 */
export default function TankovaniModal({ vozidlo, ridic, onClose, onSaved, isDark, cardStyle, isMobile = false }) {
    const [typyPaliv, setTypyPaliv] = useState([]);
    const [pumpy, setPumpy] = useState([]);
    const [novaPumpa, setNovaPumpa] = useState(null); // null | { ico, nazev, adresa, lookupLoading, lookupError, saving }
    const [form, setForm] = useState(() => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const localIso = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
        return {
            datum: localIso,
            dodavatel_id: '',
            litry: '',
            cena_kc_litr: '',
            celkem_kc: '',
            typ_paliva_id: vozidlo?.typ_paliva_id ? String(vozidlo.typ_paliva_id) : '',
            km_stav: vozidlo?.km_stav ? String(vozidlo.km_stav) : '',
            zamestnanec_id: vozidlo?.aktualni_ridic_id ? String(vozidlo.aktualni_ridic_id) : '',
            poznamka: '',
        };
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetch('/api/flotila/ciselniky')
            .then((r) => r.json())
            .then((d) => { if (!cancelled) setTypyPaliv(Array.isArray(d?.typy_paliv) ? d.typy_paliv : []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const loadPumpy = () => {
        return fetch('/api/dodavatele?kategorie=palivo&aktivni=1')
            .then((r) => r.json())
            .then((d) => {
                if (Array.isArray(d)) setPumpy(d);
                return Array.isArray(d) ? d : [];
            })
            .catch(() => []);
    };
    useEffect(() => { loadPumpy(); }, []);

    // Předvyplň cenu z poslední tankování auta — když uživatel nemá rychle po
    // ruce účtenku, často zná jen litry/celkem a cena se za posledních X dní moc nezměnila.
    useEffect(() => {
        if (!vozidlo?.id) return;
        let cancelled = false;
        fetch(`/api/flotila/tankovani?vozidlo_id=${vozidlo.id}`)
            .then((r) => r.json())
            .then((d) => {
                if (cancelled || !Array.isArray(d) || d.length === 0) return;
                const last = d[0];
                setForm((f) => f.cena_kc_litr === '' ? { ...f, cena_kc_litr: String(last.cena_kc_litr ?? '') } : f);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [vozidlo?.id]);

    // Celkem je jen vypočítané z litry × cena/L — read-only displej. Editovatelné
    // jsou jen litry a cena/L (jednosměrný sync).
    useEffect(() => {
        const litry = parseFloat(form.litry);
        const cena = parseFloat(form.cena_kc_litr);
        if (Number.isFinite(litry) && Number.isFinite(cena)) {
            const noveCelkem = (litry * cena).toFixed(2);
            if (noveCelkem !== form.celkem_kc) {
                setForm((f) => ({ ...f, celkem_kc: noveCelkem }));
            }
        } else if (form.celkem_kc !== '') {
            setForm((f) => ({ ...f, celkem_kc: '' }));
        }
    }, [form.litry, form.cena_kc_litr]);

    const change = (k) => (e) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, [k]: value }));
    };

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

    const aresLookup = async () => {
        if (!novaPumpa?.ico) return;
        setNovaPumpa((p) => ({ ...p, lookupLoading: true, lookupError: null }));
        try {
            const r = await fetch(`/api/dodavatele/ares-lookup?ico=${encodeURIComponent(novaPumpa.ico)}`, { credentials: 'same-origin' });
            const data = await r.json();
            if (!r.ok) throw new Error(data?.error || 'ARES lookup selhal');
            setNovaPumpa((p) => ({
                ...p,
                lookupLoading: false,
                lookupError: null,
                nazev: data.nazev || p.nazev || '',
                adresa: data.adresa || p.adresa || '',
            }));
        } catch (err) {
            setNovaPumpa((p) => ({ ...p, lookupLoading: false, lookupError: err.message }));
        }
    };

    const ulozitNovouPumpu = async () => {
        if (!novaPumpa?.nazev?.trim()) {
            setNovaPumpa((p) => ({ ...p, lookupError: 'Název je povinný' }));
            return;
        }
        setNovaPumpa((p) => ({ ...p, saving: true, lookupError: null }));
        try {
            const r = await fetch('/api/dodavatele', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), 'Accept': 'application/json' },
                body: JSON.stringify({
                    nazev: novaPumpa.nazev.trim(),
                    ico: novaPumpa.ico || null,
                    adresa: novaPumpa.adresa || null,
                    kategorie: ['palivo'],
                    aktivni: true,
                }),
            });
            const data = await r.json();
            if (!r.ok) {
                const firstErr = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
                throw new Error(firstErr || data?.error || 'Uložení selhalo');
            }
            await loadPumpy();
            setForm((f) => ({ ...f, dodavatel_id: String(data.id) }));
            setNovaPumpa(null);
        } catch (err) {
            setNovaPumpa((p) => ({ ...p, saving: false, lookupError: err.message }));
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const payload = {
                vozidlo_id: vozidlo.id,
                dodavatel_id: form.dodavatel_id ? Number(form.dodavatel_id) : null,
                zamestnanec_id: form.zamestnanec_id ? Number(form.zamestnanec_id) : null,
                datum: form.datum,
                km_stav: Number(form.km_stav),
                litry: Number(form.litry),
                cena_kc_litr: Number(form.cena_kc_litr),
                typ_paliva_id: Number(form.typ_paliva_id),
                poznamka: form.poznamka || null,
            };
            const r = await fetch('/api/flotila/tankovani', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), 'Accept': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify(payload),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                const firstError = data?.errors ? Object.values(data.errors)[0]?.[0] : null;
                throw new Error(firstError || data?.message || data?.error || 'Uložení selhalo');
            }
            const saved = await r.json();
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            setError(err.message || 'Uložení selhalo');
        } finally {
            setSaving(false);
        }
    };

    const _colors = getTextColors(isDark);
    const _inner = getInnerCardSurface(isDark);
    const labelColor = _colors.textMuted;
    const subtle = _colors.textSecondary;
    const textColor = _colors.textPrimary;

    // Sjednocený inputStyle podle SheetInput tokens — h:44, border 10, body font.
    // Bez focus accent ringu (to vyžaduje state per-pole, na refactor přijde Phase 2.5).
    const inputStyle = {
        width: '100%',
        height: SHEET_TOKENS.sizes.inputHeight,
        background: _inner.background,
        color: textColor,
        border: `1px solid ${_inner.border.split(' ').pop()}`,
        borderRadius: SHEET_TOKENS.radius.input,
        padding: '0 12px',
        fontSize: SHEET_TOKENS.typography.body.size,
        fontWeight: SHEET_TOKENS.typography.body.weight,
        outline: 'none',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    };
    const monoInput = { ...inputStyle, fontVariantNumeric: 'tabular-nums' };

    // `/api/flotila/ridici` vrací `cele_jmeno` (plný řetězec) i `jmeno` (s iniciálou
    // příjmení). Bereme nejvíc kompletní variantu, která je k dispozici.
    const driverName = ridic ? (ridic.cele_jmeno || ridic.jmeno || `${ridic.prijmeni || ''}`.trim()) : null;

    const podtitul = [vozidlo?.spz, vozidlo?.model, driverName].filter(Boolean).join(' · ');
    return (
        <ModalShell onClose={onClose} isDark={isDark} ikona={Fuel} barva="#FF8A00" nadpis="Nové tankování" sirka={480}>
            <form onSubmit={submit} style={{
                padding: SHEET_TOKENS.spacing.sheetPadX, display: 'flex', flexDirection: 'column', gap: 12, color: textColor,
            }}>
                {podtitul && <p style={{ margin: 0, fontSize: 12, color: labelColor }}>{podtitul}</p>}

                <Field label="Datum a čas" required labelColor={labelColor}>
                    <input className="cockpit-input" style={inputStyle} type="datetime-local" value={form.datum}
                        onChange={change('datum')} required />
                </Field>

                <Field label="Pumpa" labelColor={labelColor}>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <select
                            style={{ ...inputStyle, flex: 1 }}
                            value={form.dodavatel_id}
                            onChange={change('dodavatel_id')}
                        >
                            <option value="">— neurčeno —</option>
                            {pumpy.map((p) => (
                                <option key={p.id} value={p.id}>{p.nazev}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => setNovaPumpa({ ico: '', nazev: '', adresa: '', lookupLoading: false, lookupError: null, saving: false })}
                            title="Přidat novou pumpu"
                            style={{
                                background: 'rgba(12,192,223,0.10)',
                                color: SECTION_COLOR,
                                border: '1px solid rgba(12,192,223,0.30)',
                                borderRadius: 7, padding: '0 10px',
                                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <Plus size={13} /> Nová
                        </button>
                    </div>
                </Field>

                {novaPumpa && (
                    <div style={{
                        padding: 12,
                        borderRadius: 9,
                        background: isDark ? 'rgba(12,192,223,0.06)' : 'rgba(12,192,223,0.04)',
                        border: `1px dashed ${isDark ? 'rgba(12,192,223,0.30)' : 'rgba(12,192,223,0.40)'}`,
                        display: 'flex', flexDirection: 'column', gap: 8,
                    }}>
                        <div style={{
                            fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                            color: SECTION_COLOR,
                        }}>
                            Nová pumpa
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <input
                                style={{ ...inputStyle, fontVariantNumeric: 'tabular-nums', flex: 1 }}
                                inputMode="numeric"
                                placeholder="IČO (volitelně)"
                                value={novaPumpa.ico}
                                onChange={(e) => setNovaPumpa((p) => ({ ...p, ico: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                            />
                            <button
                                type="button"
                                onClick={aresLookup}
                                disabled={!novaPumpa.ico || novaPumpa.lookupLoading || novaPumpa.ico.length !== 8}
                                style={{
                                    background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                    color: textColor,
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                                    borderRadius: 7, padding: '0 10px',
                                    fontSize: 12, fontWeight: 600,
                                    cursor: (!novaPumpa.ico || novaPumpa.lookupLoading) ? 'not-allowed' : 'pointer',
                                    opacity: (!novaPumpa.ico || novaPumpa.lookupLoading) ? 0.55 : 1,
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {novaPumpa.lookupLoading
                                    ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                                    : <Search size={12} />}
                                ARES
                            </button>
                        </div>
                        <input
                            className="cockpit-input" style={inputStyle}
                            placeholder="Název *"
                            value={novaPumpa.nazev}
                            onChange={(e) => setNovaPumpa((p) => ({ ...p, nazev: e.target.value }))}
                            required
                        />
                        <input
                            className="cockpit-input" style={inputStyle}
                            placeholder="Adresa (volitelně)"
                            value={novaPumpa.adresa}
                            onChange={(e) => setNovaPumpa((p) => ({ ...p, adresa: e.target.value }))}
                        />
                        {novaPumpa.lookupError && (
                            <div style={{
                                fontSize: 11, color: '#EF4444',
                                padding: '4px 8px', borderRadius: 5,
                                background: 'rgba(239,68,68,0.10)',
                            }}>
                                {novaPumpa.lookupError}
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                            <button
                                type="button"
                                onClick={() => setNovaPumpa(null)}
                                style={{
                                    background: 'transparent', color: subtle,
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                                    padding: '6px 12px', borderRadius: 6,
                                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                }}
                            >
                                Zrušit
                            </button>
                            <button
                                type="button"
                                onClick={ulozitNovouPumpu}
                                disabled={novaPumpa.saving}
                                style={{
                                    background: SECTION_COLOR,
                                    color: '#fff', border: 'none',
                                    padding: '6px 14px', borderRadius: 6,
                                    fontSize: 12, fontWeight: 700,
                                    cursor: novaPumpa.saving ? 'wait' : 'pointer',
                                    opacity: novaPumpa.saving ? 0.6 : 1,
                                }}
                            >
                                {novaPumpa.saving ? 'Ukládám…' : 'Uložit pumpu'}
                            </button>
                        </div>
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <Field label="Litry" required labelColor={labelColor}>
                        <input className="cockpit-input" style={monoInput} type="number" step="0.01" min="0.01" inputMode="decimal"
                            value={form.litry} onChange={change('litry')} required autoFocus />
                    </Field>
                    <Field label="Cena/L" required labelColor={labelColor}>
                        <input className="cockpit-input" style={monoInput} type="number" step="0.01" min="0.01" inputMode="decimal"
                            value={form.cena_kc_litr} onChange={change('cena_kc_litr')} required />
                    </Field>
                    <Field label="Celkem Kč" labelColor={labelColor}>
                        <input
                            style={{ ...monoInput, opacity: 0.7, cursor: 'not-allowed' }}
                            type="number"
                            value={form.celkem_kc}
                            disabled
                            tabIndex={-1}
                            placeholder="—"
                        />
                    </Field>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Typ paliva" required labelColor={labelColor}>
                        <select className="cockpit-input" style={inputStyle} value={form.typ_paliva_id}
                            onChange={change('typ_paliva_id')} required>
                            <option value="" disabled>—</option>
                            {typyPaliv.map((p) => (
                                <option key={p.id} value={p.id}>{p.nazev}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Tachometr (km)" required labelColor={labelColor}>
                        <input className="cockpit-input" style={monoInput} type="number" step="1" min="0" inputMode="numeric"
                            value={form.km_stav} onChange={change('km_stav')} required />
                    </Field>
                </div>

                <Field label="Poznámka" labelColor={labelColor}>
                    <input className="cockpit-input" style={inputStyle} value={form.poznamka}
                        onChange={change('poznamka')}
                        placeholder="Např. Shell Frenštát, plná nádrž…" />
                </Field>

                {error && (
                    <div style={{
                        padding: '8px 12px', borderRadius: 7, fontSize: 12,
                        background: 'rgba(239,68,68,0.12)', color: '#EF4444',
                        border: '1px solid rgba(239,68,68,0.30)',
                    }}>
                        {error}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                    <SheetButton
                        isDark={isDark}
                        variant="secondary"
                        onClick={onClose}
                        fullWidth={false}
                    >Zrušit</SheetButton>
                    <SheetButton
                        isDark={isDark}
                        variant="primary"
                        type="submit"
                        busy={saving}
                        busyLabel="Ukládám…"
                        fullWidth={false}
                    >Uložit tankování</SheetButton>
                </div>
            </form>
        </ModalShell>
    );
}
